/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useContext } from 'react';
import styled from 'styled-components';
import { Modal, Spin, ListingDetailsComponents, Card, theme } from 'components';
import { UserFavoritesContext } from 'contexts';
import { featureEnabled } from 'utils';
import { zPositions } from '@/constants';
// TODO: Move this component to shared components logic (PageComponent OR FE-commons repo)
import AppointmentDetailContainer from '@app/business/ManageAppointmentPage/AppointmentDetailContainer';
import { CoopCancelReasonType } from '@app/DashboardComponents/CoopCancelAppointmentComponent';
import { useBreakpoint } from 'hooks';
import TourEditContext from '../TourEditContext';
import TourListingContext from '../TourListingContext';
import TimeSelectorDropdownOverlay from '../TourSideNav/TimeSelectorDropdownOverlay';
import TourListingModalFooter from './TourListingModalFooter';
import TourListingModalTitle from './TourListingModalTitle';
import { TourModalType } from '../types';
import { timeFormatter } from '../utils';
import { useWindowHeight, useTemporaryStyle } from '../hooks';
import { InternalShowingDetailsPage } from '../../ShowingDetailsPage';

const { ListingDetailsPage } = ListingDetailsComponents;

const TourListingModal: React.FC = () => {
  const {
    loading,
    modalType,
    setModalType,
    setAppointmentId,
    listingModalOpen,
    modalFromSearchBar,
    setListingModalState,
    selectedTourItem,
    setSelectedTourItem,
    selectedTimes,
    listing,
    photos,
    attachments,
    appointment,
  } = useContext(TourListingContext);
  const { cancelSubmittedAppointment, acceptSuggestedTime, denySuggestedTime } =
    useContext(TourEditContext);
  const { favorites, toggleFavorite } = useContext(UserFavoritesContext);

  const { lg } = useBreakpoint();
  const height = useWindowHeight();
  const closeAppointmentModal = () => {
    setAppointmentId('');
    setListingModalState(false);
    setSelectedTourItem(null);
  };

  // stop body scroll on iOS
  useTemporaryStyle(() => document.body, !lg && { position: 'fixed' }, [!lg]);

  const modalContainer = modalFromSearchBar
    ? document.getElementById('tour-universal-search')
    : document.body;

  let modalContent = <Spin />;
  // Listing Details Modal
  if (!loading && modalType === TourModalType.LISTING && listing) {
    modalContent = (
      <ListingDetailsPage
        listing={listing}
        photos={photos}
        attachments={attachments}
        fromTourPage
        closeModal={() => setListingModalState(false)}
        isFavorite={listing?.id && favorites?.has(listing.id)}
        toggleFavorite={() => toggleFavorite(listing?.id)}
      />
    );
  }
  // Appointment Details Modal
  if (!loading && modalType === TourModalType.APPOINTMENT && appointment) {
    const tourFunctions = {
      rescheduleFromTour: () => setModalType(TourModalType.RESCHEDULE),
      cancelFromTour: async (
        reason: CoopCancelReasonType,
        details: string,
        onConfirm?: () => void,
      ) => {
        await cancelSubmittedAppointment(appointment.id, reason, details);
        if (onConfirm) {
          onConfirm();
        }
        closeAppointmentModal();
      },
      acceptSuggestionFromTour: async () => {
        await acceptSuggestedTime(appointment.id);
        closeAppointmentModal();
      },
      denySuggestionFromTour: () => {
        denySuggestedTime(appointment.id);
        closeAppointmentModal();
      },
      closeAppointmentModal,
    };

    if (featureEnabled('showingDetailsRevampFeaturePhase', 1)) {
      modalContent = (
        <InternalShowingDetailsPage
          appointmentId={appointment.id}
          tourFunctions={tourFunctions}
          fromTourPage
        />
      );
    } else {
      modalContent = (
        <AppointmentDetailContainer
          appointment={appointment}
          updateAppointment={() => {
            //
          }}
          {...tourFunctions}
          listing={listing}
        />
      );
    }
  }
  // Reschedule Appointment Modal
  if (
    !loading &&
    modalType === TourModalType.RESCHEDULE &&
    selectedTourItem &&
    selectedTimes[0] &&
    appointment
  ) {
    const { status, originalTime, originalTimeEnd } = selectedTourItem;
    const { timezone } = listing;
    const [time, timeEnd] = selectedTimes;
    let startTime = timeFormatter(time, timezone);
    let endTime = timeFormatter(timeEnd, timezone);

    if (status === 'time_change' && originalTime && originalTimeEnd) {
      startTime = timeFormatter(originalTime, timezone);
      endTime = timeFormatter(originalTimeEnd, timezone);
    }
    modalContent = (
      <TimeSelectorDropdownOverlay
        listingId={appointment.listingId}
        listingTimezone={timezone}
        startTime={time}
        endTime={timeEnd}
        itemId={selectedTourItem.id}
        fromModal
      />
    );
  }

  return (
    <StyledModal
      $lg={lg}
      destroyOnClose
      open={listingModalOpen}
      title={<TourListingModalTitle />}
      width={
        !lg
          ? '100%'
          : modalType === TourModalType.RESCHEDULE
          ? Math.max(window.innerWidth / 6, 357)
          : Math.max(window.innerWidth * 0.9, 700)
      }
      bodyStyle={
        !lg
          ? {
              padding: 0,
              flexGrow: 1,
              height: '400px',
            }
          : {
              padding: '0px',
              height:
                modalType === TourModalType.LISTING
                  ? Math.max(window.innerHeight * 0.7, 400)
                  : 'auto',
            }
      }
      footer={<TourListingModalFooter />}
      onCancel={() => {
        closeAppointmentModal();
      }}
      zIndex={zPositions.tourModal}
      $height={height}
      getContainer={modalContainer}
    >
      <StyledModalBody
        className="global--angular"
        $noPadding={modalType === TourModalType.RESCHEDULE}
        $hiddenOverflow={modalType === TourModalType.RESCHEDULE}
        $lg={lg}
      >
        {lg ? (
          modalContent
        ) : (
          <Card
            bodyStyle={{
              padding: modalType === TourModalType.RESCHEDULE ? 0 : 10,
            }}
          >
            {modalContent}
          </Card>
        )}
      </StyledModalBody>
    </StyledModal>
  );
};

export default TourListingModal;

const StyledModalBody = styled.div<{
  $noPadding?: boolean;
  $hiddenOverflow?: boolean;
  $lg?: boolean;
}>`
  height: 100%;
  overflow-y: scroll;
  overflow-x: hidden;
  ${({ $noPadding, $lg }) =>
    !$noPadding && $lg && 'padding: 24px 30px !important;'}
  ${({ $lg, $noPadding }) =>
    !$lg &&
    !$noPadding &&
    `&& {padding: 15px; background-color: ${theme.layoutBodyBackground};}`}
    
  ${({ $hiddenOverflow }) => $hiddenOverflow && 'overflow: hidden;'}

  .listing-details-component-details,
  .listing-details-component-property {
    ${({ $lg }) => !$lg && 'padding-left: 0;'}
  }
`;

const StyledModal = styled(Modal)<{ $lg: boolean; $height: number }>`
  ${({ $lg, $height }) =>
    !$lg
      ? `top: 0;
         margin: 0;
         max-width: 100vw;
         height: ${$height}px;
         padding: 0;
         .ant-modal-content {
           display: flex;
           height: 100%;
           flex-direction: column;
         }
         .ant-modal-close{
           display: none;
         }
         .ant-modal-header{
           padding: 0;
         }
         `
      : `top: 50px`}
`;
